import { useTranslation } from 'react-i18next'
import { TriangleAlert } from 'lucide-react'
import { Alert, AlertDescription } from '@/components/ui/alert'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { FieldLabel } from '@/components/FieldLabel'
import { Skeleton } from '@/components/ui/skeleton'
import type { Layout } from '@/services/sites/layoutsApi'

interface RetailLayoutFieldProps {
  layouts: Layout[]
  loading: boolean
  value: string
  onChange: (layoutId: string) => void
  disabled?: boolean
}

/**
 * The retail layout new retail creatives use. Nothing to pick when the network
 * has one; a warning when it has none, as no creative can be made without it.
 */
export function RetailLayoutField({
  layouts,
  loading,
  value,
  onChange,
  disabled,
}: RetailLayoutFieldProps) {
  const { t } = useTranslation()

  if (loading) return <Skeleton className="h-9 w-full" />

  if (layouts.length === 0) {
    return (
      <Alert variant="warning">
        <TriangleAlert />
        <AlertDescription>{t('retailProducts.noRetailLayout')}</AlertDescription>
      </Alert>
    )
  }

  if (layouts.length === 1) return null

  return (
    <div className="flex flex-col gap-1.5">
      <FieldLabel>{t('retailProducts.layout')}</FieldLabel>
      <Select value={value} onValueChange={onChange} disabled={disabled}>
        <SelectTrigger>
          <SelectValue>{layouts.find((l) => l.id === value)?.name}</SelectValue>
        </SelectTrigger>
        <SelectContent>
          {layouts.map((layout) => (
            <SelectItem key={layout.id} value={layout.id}>
              {layout.name}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  )
}
